import React from 'react';
import SurveyDemo from './survey-demo/SurveyDemo';
import inMomentLogoImage from '../img/case-studies/InMoment/InMomentLogo_white.svg';
import toolLandingImage from '../img/case-studies/InMoment/toolLanding.png';
import themeBuilderExportImage from '../img/case-studies/InMoment/themeBuilderExport.png';
import ratingScaleExamplesImage from '../img/case-studies/InMoment/ratingScaleExamples.jpg';
import ratingStyleSelectorImage from '../img/case-studies/InMoment/ratingStyleSelector.png';
import campaignBuilderPreviewImage from '../img/case-studies/InMoment/campaignBuilderPreview.png';
import feedbackSurveyImage from '../img/case-studies/InMoment/feedbackSurvey.png';

function InMoment() {
  return (
    <section id="caseStudy_inMoment" className="bg-light">
      <div className="bannerArea">
        <div className="bannerContent">
          <img alt="InMoment Logo" src={inMomentLogoImage} />
        </div>
      </div>
      <div className="container">
        <div className="row py-5">
          <div className="col-12 col-md-8">
            <h2>About</h2>
            <p>
              At InMoment, my team built and maintained the custom survey themes and email campaigns used by clients across dozens of industries. Despite the similarities in the questions being asked, every client had their own colors, fonts, imagery, established brand guidelines and preferred question styles, so themes couldn't be one-size-fits-all. With the goal of creating a scalable template that offered enough versatility to meet client needs, I led development of a set of internal tools that let non-developers configure a theme visually and export the code the platform needed, closing the gap between design and implementation. The same kind of theming and personalization work continued later at Press Ganey Forsta and Qualtrics.
            </p>
            <h2>The Challenge</h2>
            <p>
              Branded survey themes at InMoment were built from custom CSS, JavaScript, and HTML layered on top of the platform's established DOM, question types came with their own baseline functionality and a basic set of default styles, and everything my team built extended and restyled that foundation. That gave us plenty of flexibility, but it also meant every theme required either a developer's time or deep familiarity with the underlying code, something client success managers and non-technical designers didn't have. We needed a way to expose just the pieces that actually varied, colors, fonts, logos, question styles, without requiring anyone to understand the template system underneath.
            </p>
          </div>
          <div className="col-12 col-md-4">
            <section id="projectComponents" className="pt-5 pt-md-0 px-0 px-md-4">
              <div>
                <h3>Project Components:</h3>
                <ul>
                  <li>Demo Showcase Survey</li>
                  <li>Survey Theme Builder</li>
                  <li>Email Campaign Builder</li>
                  <li>Feedback Survey</li>
                </ul>
              </div>
              <h3>My Role</h3>
              <ul>
                <li>Project Planning</li>
                <li>Design &amp; Development</li>
                <li>Announcement &amp; Deployment</li>
              </ul>
            </section>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="row py-5 px-4 bg-white--rounded align-items-center">
          <div className="col-12 col-md-5">
            <div className="case-study-image">
              <img className="img-fluid rounded shadow" src={toolLandingImage} alt="A landing screen offering four tools: Demo Showcase Survey, Theme Builder Survey, Campaign Builder Survey, and Leave Feedback" />
              <span className="image-caption">Each tool started from a shared landing screen</span>
            </div>
          </div>
          <div className="col-12 col-md-7">
            <h2>The Approach</h2>
            <p>
              The tool suite was organized as four entry points from one landing screen: a demo showcase survey, the theme builder, a campaign builder for emails, and a feedback survey covering all three.
            </p>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="row py-5 align-items-center">
          <div className="col-12 col-md-6">
            <h3>The Theme Builder</h3>
            <p>
              The Theme Builder Survey was the core tool of this project, delivered as a step by step survey that allowed users to make selections for their preferred colors, fonts, imagery, layout and more, all updating in real time as selections are made giving a live example of how their custom theme would impact various question types. Finishing a theme produced a working export of the variables and HTML snippets needed to drop straight into the platform, providing an implementation-ready config, and a custom theme ready to go, rather than a mockup someone still had to translate into code.
            </p>
          </div>
          <div className="col-12 col-md-6">
            <div className="case-study-image">
              <img className="img-fluid rounded shadow" src={themeBuilderExportImage} alt="The Theme Builder's completion screen exporting LESS variables and HTML" />
              <span className="image-caption">Finishing a theme exports the LESS and HTML needed to implement it</span>
            </div>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="row py-5 px-4 bg-white--rounded align-items-center">
          <div className="col-12 col-md-3">
            <div className="case-study-image case-study-image--device">
              <img className="img-fluid" src={ratingStyleSelectorImage} alt="A mobile screen for choosing between rating scale styles" />
              <span className="image-caption">Choosing a rating style on mobile</span>
            </div>
          </div>
        
          <div className="col-12 col-md-5">
            <h3>Theme Flexibility</h3>
            <p>
              Among the settings available in the theme builder is the ability to control exactly how a rating scale is displayed.

              Within the settings of our templates, rating scales could be presented as numbered segmented buttons, labelled stacked buttons, plain radio buttons, or CTA-style buttons, with the style swappable per client without touching markup. For prompts where it made sense, my team also built custom scale types like stars and emojis, so a single prompt could use that presentation without forcing every rating scale in the survey to follow suit.
            </p>
          </div>

          <div className="col-12 col-md-4">
            <div className="case-study-image">
              <img className="img-fluid rounded" src={ratingScaleExamplesImage} alt="The same rating scale rendered as emoji faces, numbered segmented buttons, and a pill-shaped scale" />
              <span className="image-caption">A few of the selectable rating scale styles</span>
            </div>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="row py-5 px-4 align-items-center">
          <div className="col-12 col-md-6">
            <h2>The Campaign Builder</h2>
            <p>
              The same theming approach carried over to email: a Campaign Builder with Settings and Branding controls on one side and a live preview of the email on the other, so changes to logo, colors, and copy were visible immediately instead of requiring a send-to-self test.
            </p>
          </div>
          <div className="col-12 col-md-6">
            <div className="case-study-image">
              <img className="img-fluid rounded shadow" src={campaignBuilderPreviewImage} alt="The Campaign Builder's branding panel next to a live preview of an email campaign" />
              <span className="image-caption">Branding controls next to a live email preview</span>
            </div>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="row py-5 align-items-center">
          <div className="col-12 col-md-5">
            <div className="case-study-image">
              <img className="img-fluid rounded shadow" src={feedbackSurveyImage} alt="A feedback survey asking which tools were used and how satisfied users were" />
              <span className="image-caption">The feedback survey used to improve the tools themselves</span>
            </div>
          </div>
          <div className="col-12 col-md-7">
            <h2>Closing the Loop</h2>
            <p>
              To help guide future development of our templates, the tool suite also included a feedback survey asking people which of the tools they'd used and how satisfied they were with the results. This was a small but deliberate way of treating our internal users the same way we asked our clients to treat theirs and helped to shape further enhancements.
            </p>
          </div>
        </div>
      </div>

      <div className="highlightSection">
        <div className="container">
          <div className="row py-5">
            <div className="col-12">
              <h2>The Impact</h2>
              <p>
                These tools cut the time to stand up a new client theme from days of back-and-forth to something one person could do directly if the client was looking for more of a DIY approach. The available style options were built with accessibility in mind from the start, and careful consideration was given to help ensure that the created survey themes were kept WCAG compliant.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="row py-5">
          <div className="col-12">
            <h2>Try It Yourself</h2>
            <p>
              Below is a simplified, self-contained example that showcases this sort of theming work and acts as an example of how survey themes are custom tailored for each client and the sort of versatility you can see in a survey. The demo provides an example survey that can be re-skinned between different brand identities and some of the range of flexibility that needs to be accounted for. Use the dropdown to switch brands and step through the questions.
            </p>
          </div>
        </div>
        <div className="row pb-5">
          <div className="col-12">
            <SurveyDemo />
          </div>
        </div>
      </div>

      <div className="container">
        <div className="row py-5">
          <div className="col-12">
            <h2>Conclusion</h2>
            <p>
              This project shaped how I think about internal tools generally, as it focused on creating reusable, scalable solutions that could work to establish a baseline for any brand. The goal wasn't to give the user a blank canvas, or to overwhelm with customization options. It would be easy to expose every variable to the user so that they could choose their exact font size and border radius, but at that point they may as well be writing the code. We instead opted for an approach that focused on exposing exactly the knobs that need turning and defaulting everything else sensibly.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default InMoment;
